import React, { useEffect } from "react";
import { Nullable } from "sonobello.utilities.react";

import { IExpiredReservationViewRouter } from "../../../Routing/Types/IRouter";
import IReservation from "../../../Types/IReservation";

export interface IReservationGuarantorDecoratorProps {
  /** The volatile reservation which may be null. */
  reservation: Nullable<IReservation>;
  /** The router to navigate to the expired reservation view if needed. */
  router: IExpiredReservationViewRouter;
  /** The session key for the current session. */
  sessionKey: string;
}

export interface IReservationGuarantorDecoratorConfig<TComponentProps extends { reservation: IReservation }> {
  /** The child component which requires an existing {@link IReservation}. */
  ChildComponent: React.FC<TComponentProps>;
}

/**
 * Guarantees that the reservation exists before rendering the child component. If it doesn't exist, the user is routed
 * to the expired reservation view.
 */
const ReservationGuarantorDecorator = <TChildProps extends { reservation: IReservation }>({
  reservation,
  router,
  sessionKey,
  ChildComponent,
  ...rest
}: IReservationGuarantorDecoratorProps &
  IReservationGuarantorDecoratorConfig<TChildProps> &
  Omit<TChildProps, keyof IReservationGuarantorDecoratorProps | "ChildComponent">): React.ReactElement => {
  useEffect(() => {
    if (!reservation) router.goToExpiredReservationView({ sessionKey });
  }, [reservation, router, sessionKey]);
  return reservation ? (
    <ChildComponent {...({ ...rest, reservation, router } as unknown as TChildProps)} />
  ) : (
    <React.Fragment />
  );
};

export default ReservationGuarantorDecorator;
